
import React, { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/use-toast";
import { Home, Settings, Menu, Moon, Sun, Facebook, Instagram, Youtube } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import CookieConsent, { CookiePreferencesWidget } from "./CookieConsent";
import LatestInsertions from "./LatestInsertions";
import { useTheme } from "@/providers/ThemeProvider";
import logoImage from "@/assets/affarefatto-logo-grande.webp";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  // Pagine legali
  const isLegalPage = useMemo(() => {
    const legalPaths = ['/privacy-policy', '/cookie-policy', '/terms-of-service'];
    return legalPaths.includes(location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col pt-[2cm]">
      <header className="fixed top-0 left-0 right-0 h-[2cm] text-black shadow-md z-50 bg-gradient-to-r from-[hsl(var(--blue-pastel))] via-[hsl(var(--primary))] via-[hsl(var(--secondary))] via-[hsl(var(--orange-pastel))] via-[hsl(var(--red-pastel))] to-[hsl(var(--accent))] overflow-hidden">
        <div className="container mx-auto h-full flex justify-center relative">
          <div className="absolute left-0 h-full flex items-center gap-1 sm:gap-2">
            {!isHomePage && (
              <Link 
                to="/" 
                className="p-1.5 sm:p-2 rounded-full hover:bg-white/20 transition-colors"
                aria-label="Torna alla Home"
              >
                <Home size={20} className="sm:w-6 sm:h-6" aria-hidden="true" />
              </Link>
            )}
            
            {/* Menu Hamburger per Mobile */}
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger 
                className="lg:hidden p-1.5 sm:p-2 rounded-full hover:bg-white/20 transition-colors"
                aria-label="Apri menu di navigazione"
              >
                <Menu size={20} className="sm:w-6 sm:h-6" aria-hidden="true" />
              </SheetTrigger>
              <SheetContent side="left" className="w-64 bg-background">
                <div className="flex flex-col space-y-4 mt-8">
                  {/* Toggle Tema Mobile */}
                  <button
                    onClick={toggleTheme}
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    {theme === 'dark' ? (
                      <>
                        <Sun className="w-5 h-5" />
                        <span>Modalità Chiara</span>
                      </>
                    ) : (
                      <>
                        <Moon className="w-5 h-5" />
                        <span>Modalità Scura</span>
                      </>
                    )}
                  </button>
                  <div className="border-t border-border my-2" />
                  <Link 
                    to="/about" 
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-muted transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="text-xl">ℹ️</span>
                    <span>Chi Siamo</span>
                  </Link>
                  <Link 
                    to="/faq" 
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-muted transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="text-xl">❓</span>
                    <span>FAQ</span>
                  </Link>
                  <div className="border-t border-border my-2" />
                  <Link 
                    to="/privacy-policy" 
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-muted transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="text-xl">🔒</span>
                    <span>Privacy Policy</span>
                  </Link>
                  <Link 
                    to="/cookie-policy" 
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-muted transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="text-xl">🍪</span>
                    <span>Cookie Policy</span>
                  </Link>
                  <Link 
                    to="/terms-of-service" 
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-muted transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="text-xl">📜</span>
                    <span>Termini di Servizio</span>
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
          
          <Link to="/" className="flex items-center h-full" aria-label="AffareFatto - Torna alla Home">
            <img 
              src={logoImage} 
              alt="AffareFatto.info - Logo" 
              className="h-[3cm] w-auto"
              width="353"
              height="198"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </Link>
          
          {/* Menu Desktop */}
          <div className="absolute right-0 h-full flex items-center hidden lg:flex">
            <DropdownMenu>
              <DropdownMenuTrigger 
                className="p-2 rounded-full hover:bg-white/20 transition-colors focus:outline-none"
                aria-label="Apri menu impostazioni"
              >
                <Settings size={24} aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-background dark:bg-gray-800 border shadow-lg z-50">
                <DropdownMenuItem 
                  onClick={toggleTheme}
                  className="cursor-pointer"
                >
                  {theme === 'dark' ? (
                    <>☀️ Modalità Chiara</>
                  ) : (
                    <>🌙 Modalità Scura</>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link 
                    to="/about" 
                    className="cursor-pointer"
                  >
                    ℹ️ Chi Siamo
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link 
                    to="/faq" 
                    className="cursor-pointer"
                  >
                    ❓ FAQ
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link 
                    to="/privacy-policy" 
                    className="cursor-pointer"
                  >
                    🔒 Privacy Policy
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link 
                    to="/cookie-policy" 
                    className="cursor-pointer"
                  >
                    🍪 Cookie Policy
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link 
                    to="/terms-of-service" 
                    className="cursor-pointer"
                  >
                    📜 Termini di Servizio
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="flex-grow px-2 sm:px-4 pb-[60px] overflow-y-auto" role="main">
        {children}
      </main>

      {/* Cookie Consent Banner */}
      <CookieConsent />
      
      {/* Cookie Preferences Widget - allows reopening preferences */}
      <CookiePreferencesWidget />

      {/* Footer with legal links and copyright - Improved visual style */}
      <footer className="text-center py-6 text-xs" role="contentinfo">
        {/* Latest Insertions - sopra la linea colorata */}
        {!isLegalPage && <LatestInsertions />}
        
        {/* Gradient separator */}
        <div className="h-1 bg-gradient-to-r from-[hsl(var(--blue-pastel))] via-[hsl(var(--primary))] via-[hsl(var(--secondary))] via-[hsl(var(--orange-pastel))] to-[hsl(var(--accent))] mb-6" />
        
        {/* Social Media Links */}
        <div className="flex justify-center items-center gap-4 mb-4">
          <a 
            href="https://www.facebook.com/groups/affarefatto.info" 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-2 rounded-full bg-[#1877F2] text-white hover:opacity-80 transition-opacity"
            aria-label="Gruppo Facebook AffareFatto"
          >
            <Facebook size={20} />
          </a>
          <a 
            href="https://www.instagram.com/affarefatto.info/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-2 rounded-full bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white hover:opacity-80 transition-opacity"
            aria-label="Pagina Instagram AffareFatto"
          >
            <Instagram size={20} />
          </a>
          <a 
            href="https://www.youtube.com/@AffareFattoinfo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-2 rounded-full bg-[#FF0000] text-white hover:opacity-80 transition-opacity"
            aria-label="Canale YouTube AffareFatto"
          >
            <Youtube size={20} />
          </a>
        </div>
        
        <div className="flex justify-center items-center gap-3 mb-3">
          <Link to="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors duration-200 hover:underline">
            Privacy Policy
          </Link>
          <span className="text-primary/50">•</span>
          <Link to="/cookie-policy" className="text-muted-foreground hover:text-primary transition-colors duration-200 hover:underline">
            Cookie Policy
          </Link>
          <span className="text-primary/50">•</span>
          <Link to="/terms-of-service" className="text-muted-foreground hover:text-primary transition-colors duration-200 hover:underline">
            Termini e Condizioni
          </Link>
        </div>
        <div className="text-muted-foreground/70">© 2025-2026 AffareFatto. Tutti i diritti riservati.</div>
      </footer>

      <Toaster />
    </div>
  );
};

export default Layout;
